'use client';
import React, { useState } from 'react';
import AssetListPanel from './AssetListPanel';
import MapArea from './MapArea';
import AssetDetailPanel from './AssetDetailPanel';
import AssetMapModal from './AssetMapModal';
import { List, Map, Info } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// Backend integration: WebSocket connection to /ws/fleet/positions for real-time GPS updates
export type TravelPoint = {
  x: number;   // normalized 0-100 map position
  y: number;
  timestamp: string;
  speed: number;
};

export type Asset = {
  id: string;
  unitNumber: string;
  type: 'vehicle' | 'equipment' | 'person';
  make: string;
  model: string;
  driver?: string;
  status: string;
  speed: number;
  heading: string;
  lat: number;
  lng: number;
  lastPing: string;
  jobId?: string;
  jobSite?: string;
  etaMinutes?: number;
  routeProgress?: number;
  odometer: number;
  fuelLevel?: number;
  signal: 'strong' | 'weak' | 'lost';
  alerts: string[];
  travelHistory?: TravelPoint[];
  distanceTodayMiles?: number;
};

export const mockAssets: Asset[] = [
  { id: 'asset-v01', unitNumber: 'Unit-14', type: 'vehicle', make: 'Ford', model: 'F-350 Super Duty', driver: 'Travis Reyes', status: 'en-route', speed: 67, heading: 'NE', lat: 31.845, lng: -102.367, lastPing: '13:55:48', jobId: 'FJ-2841', jobSite: 'Site C, Odessa TX', etaMinutes: 22, routeProgress: 38, odometer: 84320, fuelLevel: 62, signal: 'strong', alerts: ['Geofence exit detected'],
    distanceTodayMiles: 142,
    travelHistory: [
      { x: 79, y: 18, timestamp: '06:12', speed: 0 },
      { x: 76, y: 22, timestamp: '07:30', speed: 55 },
      { x: 73, y: 27, timestamp: '08:45', speed: 62 },
      { x: 70, y: 31, timestamp: '09:50', speed: 68 },
      { x: 67, y: 34, timestamp: '11:00', speed: 71 },
      { x: 64, y: 36, timestamp: '12:20', speed: 65 },
      { x: 62, y: 38, timestamp: '13:55', speed: 67 },
    ],
  },
  { id: 'asset-v02', unitNumber: 'Unit-08', type: 'vehicle', make: 'Chevy', model: 'Silverado 2500 HD', driver: 'Maria Gutierrez', status: 'on-site', speed: 0, heading: '--', lat: 31.742, lng: -102.551, lastPing: '13:55:51', jobId: 'FJ-2838', jobSite: 'Block 14, Andrews TX', etaMinutes: 0, routeProgress: 100, odometer: 61045, fuelLevel: 78, signal: 'strong', alerts: [],
    distanceTodayMiles: 87,
    travelHistory: [
      { x: 79, y: 18, timestamp: '05:45', speed: 0 },
      { x: 72, y: 28, timestamp: '06:30', speed: 58 },
      { x: 60, y: 38, timestamp: '07:15', speed: 63 },
      { x: 48, y: 46, timestamp: '08:00', speed: 55 },
      { x: 38, y: 52, timestamp: '08:40', speed: 48 },
      { x: 34, y: 55, timestamp: '09:10', speed: 0 },
    ],
  },
  { id: 'asset-v03', unitNumber: 'Unit-22', type: 'vehicle', make: 'Dodge', model: 'Ram 2500', driver: 'Kyle Odom', status: 'offline', speed: 0, heading: '--', lat: 31.993, lng: -102.072, lastPing: '13:38:02', jobId: undefined, jobSite: undefined, etaMinutes: undefined, routeProgress: 0, odometer: 112840, fuelLevel: 45, signal: 'lost', alerts: ['GPS signal lost 17 min ago'],
    distanceTodayMiles: 23,
    travelHistory: [
      { x: 79, y: 18, timestamp: '07:00', speed: 0 },
      { x: 79, y: 19, timestamp: '08:10', speed: 22 },
      { x: 78, y: 22, timestamp: '13:38', speed: 0 },
    ],
  },
  { id: 'asset-v04', unitNumber: 'Unit-05', type: 'vehicle', make: 'Ford', model: 'F-250 XLT', driver: 'Darnell Brooks', status: 'returning', speed: 54, heading: 'SW', lat: 31.611, lng: -102.834, lastPing: '13:55:44', jobId: 'FJ-2833', jobSite: 'Compressor Stn 3', etaMinutes: 18, routeProgress: 72, odometer: 93200, fuelLevel: 88, signal: 'strong', alerts: [],
    distanceTodayMiles: 198,
    travelHistory: [
      { x: 79, y: 18, timestamp: '04:30', speed: 0 },
      { x: 65, y: 30, timestamp: '05:45', speed: 60 },
      { x: 45, y: 48, timestamp: '07:00', speed: 65 },
      { x: 22, y: 62, timestamp: '08:15', speed: 58 },
      { x: 12, y: 82, timestamp: '09:00', speed: 0 },
      { x: 14, y: 78, timestamp: '11:30', speed: 52 },
      { x: 18, y: 72, timestamp: '13:55', speed: 54 },
    ],
  },
  { id: 'asset-v05', unitNumber: 'Unit-19', type: 'vehicle', make: 'Toyota', model: 'Tundra TRD Pro', driver: 'Sofia Castillo', status: 'en-route', speed: 71, heading: 'N', lat: 31.901, lng: -102.194, lastPing: '13:55:52', jobId: 'FJ-2829', jobSite: 'FM 1788, Midland TX', etaMinutes: 5, routeProgress: 88, odometer: 43670, fuelLevel: 55, signal: 'strong', alerts: [],
    distanceTodayMiles: 116,
    travelHistory: [
      { x: 79, y: 18, timestamp: '08:00', speed: 0 },
      { x: 77, y: 21, timestamp: '09:10', speed: 55 },
      { x: 75, y: 24, timestamp: '10:20', speed: 68 },
      { x: 73, y: 26, timestamp: '11:30', speed: 72 },
      { x: 71, y: 28, timestamp: '13:55', speed: 71 },
    ],
  },
  { id: 'asset-v06', unitNumber: 'Unit-03', type: 'vehicle', make: 'Ram', model: '3500 Laramie', driver: 'James Whitfield', status: 'available', speed: 0, heading: '--', lat: 31.997, lng: -102.078, lastPing: '13:55:30', jobId: undefined, jobSite: undefined, etaMinutes: undefined, routeProgress: 0, odometer: 57120, fuelLevel: 95, signal: 'strong', alerts: [],
    distanceTodayMiles: 0,
    travelHistory: [
      { x: 79, y: 18, timestamp: '06:00', speed: 0 },
    ],
  },
  { id: 'asset-e01', unitNumber: 'EX-07', type: 'equipment', make: 'Caterpillar', model: '320 Excavator', driver: undefined, status: 'on-site', speed: 0, heading: '--', lat: 31.742, lng: -102.553, lastPing: '13:54:12', jobId: 'FJ-2838', jobSite: 'Block 14, Andrews TX', etaMinutes: 0, routeProgress: 100, odometer: 4820, fuelLevel: 34, signal: 'weak', alerts: ['Idle 48 min — verify job status'],
    distanceTodayMiles: 12,
    travelHistory: [
      { x: 40, y: 50, timestamp: '07:00', speed: 0 },
      { x: 38, y: 52, timestamp: '08:30', speed: 8 },
      { x: 35, y: 56, timestamp: '09:45', speed: 0 },
    ],
  },
  { id: 'asset-e02', unitNumber: 'CR-12', type: 'equipment', make: 'Manitowoc', model: '14000 Crawler Crane', driver: undefined, status: 'on-site', speed: 0, heading: '--', lat: 31.558, lng: -103.124, lastPing: '13:55:00', jobId: 'FJ-2825', jobSite: 'Pad 12, Loving County', etaMinutes: 0, routeProgress: 100, odometer: 2340, fuelLevel: 71, signal: 'strong', alerts: [],
    distanceTodayMiles: 8,
    travelHistory: [
      { x: 14, y: 79, timestamp: '06:30', speed: 0 },
      { x: 12, y: 82, timestamp: '08:00', speed: 5 },
    ],
  },
  { id: 'asset-p01', unitNumber: 'TECH-04', type: 'person', make: '', model: '', driver: 'Aaron Thibodeau', status: 'on-site', speed: 2, heading: 'E', lat: 31.558, lng: -103.122, lastPing: '13:55:55', jobId: 'FJ-2825', jobSite: 'Pad 12, Loving County', etaMinutes: 0, routeProgress: 100, odometer: 0, signal: 'strong', alerts: [],
    distanceTodayMiles: 94,
    travelHistory: [
      { x: 79, y: 18, timestamp: '05:30', speed: 0 },
      { x: 55, y: 38, timestamp: '07:00', speed: 60 },
      { x: 30, y: 58, timestamp: '08:30', speed: 65 },
      { x: 13, y: 81, timestamp: '09:45', speed: 0 },
    ],
  },
  { id: 'asset-p02', unitNumber: 'TECH-09', type: 'person', make: '', model: '', driver: 'Priya Nair', status: 'en-route', speed: 67, heading: 'NE', lat: 31.846, lng: -102.368, lastPing: '13:55:48', jobId: 'FJ-2841', jobSite: 'Site C, Odessa TX', etaMinutes: 22, routeProgress: 38, odometer: 0, signal: 'strong', alerts: [],
    distanceTodayMiles: 138,
    travelHistory: [
      { x: 79, y: 18, timestamp: '06:15', speed: 0 },
      { x: 76, y: 22, timestamp: '07:30', speed: 55 },
      { x: 73, y: 27, timestamp: '08:45', speed: 62 },
      { x: 70, y: 31, timestamp: '09:50', speed: 68 },
      { x: 67, y: 34, timestamp: '11:00', speed: 71 },
      { x: 64, y: 36, timestamp: '12:20', speed: 65 },
      { x: 63, y: 37, timestamp: '13:55', speed: 67 },
    ],
  },
];

type MobileTab = 'list' | 'map' | 'detail';

export default function LiveTrackingView() {
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>('asset-v01');
  const [filterType, setFilterType] = useState<'all' | 'vehicle' | 'equipment' | 'person'>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileTab, setMobileTab] = useState<MobileTab>('map');
  const [mapModalAssetId, setMapModalAssetId] = useState<string | null>(null);

  const selectedAsset = mockAssets.find((a) => a.id === selectedAssetId) ?? null;
  const mapModalAsset = mockAssets.find((a) => a.id === mapModalAssetId) ?? null;

  const handleSelectAsset = (id: string | null) => {
    setSelectedAssetId(id);
    if (id) setMobileTab('detail');
  };

  const handleOpenMapModal = (id: string) => {
    setMapModalAssetId(id);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-zinc-100">Live Tracking</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Real-time GPS positions — 10 assets tracked · Updated 13:56:02</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-full font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            GPS Live
          </span>
        </div>
      </div>

      {/* Mobile tab switcher */}
      <div className="flex lg:hidden items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 mb-3 w-fit">
        {([
          { key: 'list', label: 'Assets', icon: List },
          { key: 'map', label: 'Map', icon: Map },
          { key: 'detail', label: 'Detail', icon: Info },
        ] as { key: MobileTab; label: string; icon: React.ElementType }[]).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setMobileTab(key)}
            disabled={key === 'detail' && !selectedAsset}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${mobileTab === key ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {/* Desktop: three-panel layout */}
      <div className="hidden lg:flex gap-4 flex-1 min-h-0" style={{ height: 'calc(100vh - 200px)' }}>
        <AssetListPanel
          assets={mockAssets}
          selectedAssetId={selectedAssetId}
          onSelectAsset={setSelectedAssetId}
          onOpenMap={handleOpenMapModal}
          filterType={filterType}
          setFilterType={setFilterType}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <MapArea assets={mockAssets} selectedAssetId={selectedAssetId} onSelectAsset={(id) => { setSelectedAssetId(id); handleOpenMapModal(id); }} />
        {selectedAsset && (
          <AssetDetailPanel asset={selectedAsset} onClose={() => setSelectedAssetId(null)} />
        )}
      </div>

      {/* Mobile: single panel based on tab */}
      <div className="flex lg:hidden flex-1 min-h-0" style={{ minHeight: '60vh' }}>
        {mobileTab === 'list' && (
          <div className="w-full">
            <AssetListPanel
              assets={mockAssets}
              selectedAssetId={selectedAssetId}
              onSelectAsset={handleSelectAsset}
              onOpenMap={handleOpenMapModal}
              filterType={filterType}
              setFilterType={setFilterType}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        )}
        {mobileTab === 'map' && (
          <div className="w-full">
            <MapArea assets={mockAssets} selectedAssetId={selectedAssetId} onSelectAsset={(id) => { handleSelectAsset(id); handleOpenMapModal(id); }} />
          </div>
        )}
        {mobileTab === 'detail' && selectedAsset && (
          <div className="w-full">
            <AssetDetailPanel asset={selectedAsset} onClose={() => { setSelectedAssetId(null); setMobileTab('map'); }} />
          </div>
        )}
      </div>

      {/* Asset Map Modal */}
      {mapModalAsset && (
        <AssetMapModal asset={mapModalAsset} onClose={() => setMapModalAssetId(null)} />
      )}
    </div>
  );
}